import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API, apiJSON as defaultApiJSON } from "./apiBase";

const PAGE_SIZE = 50;
const MAX_PENDING_ERRORS = 500;
const fieldStyle = {
  background: "#0f172a", color: "#e5e7eb", border: "1px solid #475569",
  borderRadius: 8, padding: "9px 10px", minHeight: 40, maxWidth: "100%",
};
const buttonStyle = { ...fieldStyle, cursor: "pointer", fontWeight: 700 };
const cellStyle = { padding: 10, textAlign: "left", verticalAlign: "top", borderBottom: "1px solid #e2e8f0" };

function text(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

function readableTime(value) {
  if (!value) return "—";
  // SQLite's legacy UTC timestamps have no zone; explicitly interpret them as UTC.
  const raw = String(value);
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? `${raw.replace(" ", "T")}Z` : raw;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleString();
}

export function retryPresentation(item) {
  const code = text(item?.error_code);
  const action = text(item?.retry_action).toLowerCase();
  const requiresPolicy = ["130472", "131049", "131026", "132000"].includes(code);
  // The matching backend update supplies the decision. An older backend must
  // not leave these recipient restrictions looking immediately retryable.
  const missingPolicy = requiresPolicy && (!action || (code === "132000" && action === "retry"));
  const eligible = item?.can_retry === true && !missingPolicy && (code !== "132000" || action === "repair_template");
  let label = eligible ? action === "repair_template" ? "Retry corrected" : "Retry" : "Unavailable";
  if (!eligible) {
    if (missingPolicy) label = "Update required";
    else if (action === "fix_template") label = "Fix template";
    else if (["cooldown", "wait", "retry_later", "wait_until"].includes(action)) label = "Wait";
    else if (["contact_customer", "customer_message", "customer_action"].includes(action) || code === "130472") label = "Customer action";
    else if (["verify_recipient", "recipient_verification"].includes(action) || code === "131026") label = "Check recipient";
    else if (["fix_contact", "correct_contact"].includes(action)) label = "Fix contact";
    else if (["verify_delivery", "check_provider"].includes(action) || text(item?.status) === "UNKNOWN") label = "Check delivery";
    else if (action === "pending") label = "Await status";
    else if (["ACCEPTED", "SENT", "DELIVERED", "READ"].includes(text(item?.status))) label = "No retry needed";
  }
  return {
    eligible, label, action,
    retryAt: item?.retry_after_at || "",
    nextStep: missingPolicy
      ? "Install the matching backend retry update before retrying this error."
      : text(item?.next_step || item?.retry_blocked_reason),
  };
}

export function RetryGuidance({ item }) {
  const guidance = retryPresentation(item);
  return <>
    {guidance.nextStep && <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.45 }}>{guidance.nextStep}</div>}
    {item?.correction_note && <div style={{ marginTop: 6, fontSize: 12 }}><b>Correction:</b> {text(item.correction_note)}</div>}
    {guidance.retryAt && <div style={{ marginTop: 6, fontSize: 12 }}>
      <b>Earliest retry:</b> {readableTime(guidance.retryAt)}<div>Shown in your local time. Delivery is still subject to provider approval.</div>
    </div>}
  </>;
}

export function newRetryKey() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const syncingErrors = new Set();
export function historyUserKey(user) {
  return String(user?.id ?? user?.uid ?? user?.sub ?? user?.username ?? user?.email ?? "");
}
function queueKey(branch, userKey) { return `wa:unsaved-errors:v1:${API}:${branch}:${userKey}`; }
function readPending(branch, userKey) {
  try {
    const items = JSON.parse(localStorage.getItem(queueKey(branch, userKey)) || "[]");
    return Array.isArray(items) ? items.filter((item) => item && item.request_key && item.branch === branch) : [];
  } catch { return []; }
}
function writePending(branch, userKey, items) {
  if (items.length > MAX_PENDING_ERRORS) throw new Error("The browser queue is full; reconnect to sync the 500 saved errors before continuing.");
  localStorage.setItem(queueKey(branch, userKey), JSON.stringify(items));
  window.dispatchEvent(new Event("wa:pending-errors"));
}

export async function recordClientError(apiJSON, payload, userKey = "") {
  const item = {
    ...payload, request_key: String(payload.request_key || newRetryKey()).slice(0, 200),
    customer_id: text(payload.customer_id).slice(0, 128),
    customer_name: text(payload.customer_name).slice(0, 500), phone: text(payload.phone).slice(0, 100),
    template_name: text(payload.template_name).slice(0, 200), source: text(payload.source).slice(0, 80),
    search_date: text(payload.search_date).slice(0, 30), error_code: text(payload.error_code).slice(0, 100),
    error_message: text(payload.error_message).slice(0, 4000),
  };
  let storageError = "";
  try {
    const previous = readPending(item.branch, userKey);
    writePending(item.branch, userKey, [...previous.filter((entry) => entry.request_key !== item.request_key), item]);
  } catch (e) { storageError = e?.message || String(e); }
  try {
    await apiJSON("/wa360/messages/client-error", {
      method: "POST", headers: { "Content-Type": "application/json", "x-branch": item.branch },
      body: JSON.stringify(item),
    });
    try { writePending(item.branch, userKey, readPending(item.branch, userKey).filter((entry) => entry.request_key !== item.request_key)); } catch { /* Already saved on server. */ }
    return { saved: true, pending: false };
  } catch (e) {
    if (storageError) throw new Error(`Error could not be saved on the server or this browser. ${e?.message || e}; ${storageError}`);
    return { saved: false, pending: true };
  }
}

async function syncPending(apiJSON, branch, userKey, shouldContinue = () => true) {
  for (const item of readPending(branch, userKey).slice(0, 20)) {
    if (!shouldContinue()) break;
    const lock = `${branch}:${userKey}:${item.request_key}`;
    if (syncingErrors.has(lock)) continue;
    syncingErrors.add(lock);
    try {
      const outcome = await recordClientError(apiJSON, item, userKey);
      if (outcome.pending) break;
    } finally { syncingErrors.delete(lock); }
  }
}

// History is loaded from the server database on every mount. Search/reset actions
// in the parent pages never delete the message ledger.
export default function MessageHistory({ branch = "", source = "", userKey = "", apiJSON = defaultApiJSON, refreshToken = 0 }) {
  const [sourceFilter, setSourceFilter] = useState(source);
  const [status, setStatus] = useState("errors");
  const [template, setTemplate] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, templates: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [retrying, setRetrying] = useState("");
  const [pending, setPending] = useState([]);
  const [attempts, setAttempts] = useState({});
  const [attemptLoading, setAttemptLoading] = useState("");
  const requestRef = useRef(apiJSON);
  const revisionRef = useRef(0);
  const retryLockRef = useRef(false);
  const mountedRef = useRef(false);
  const contextRef = useRef("");
  const scope = `${branch}:${source}:${userKey}`;
  contextRef.current = scope;
  requestRef.current = apiJSON;

  const params = useMemo(() => {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (branch) p.set("branch", branch);
    if (sourceFilter) p.set("source", sourceFilter);
    if (status) p.set("status", status);
    if (template) p.set("template", template);
    if (errorCode) p.set("error_code", errorCode);
    if (appliedQuery) p.set("q", appliedQuery);
    if (searchDate) p.set("search_date", searchDate);
    return p.toString();
  }, [branch, sourceFilter, status, template, errorCode, appliedQuery, searchDate, offset]);

  const load = useCallback(async (silent = false) => {
    if (!branch) {
      setData({ items: [], total: 0, templates: [] });
      return;
    }
    const revision = ++revisionRef.current;
    if (!silent) setLoading(true);
    try {
      const response = await requestRef.current(`/wa360/messages?${params}`, {
        method: "GET", headers: { "x-branch": branch },
      });
      if (!mountedRef.current || revision !== revisionRef.current) return;
      setData({
        items: Array.isArray(response?.items) ? response.items : [],
        total: Number(response?.total || 0),
        templates: Array.isArray(response?.templates) ? response.templates : [],
      });
      setError("");
      // Retry only saving queued errors after a successful history read proves
      // the session and connection work. Never automatically resend a message.
      const activeScope = contextRef.current;
      await syncPending(requestRef.current, branch, userKey, () => mountedRef.current && contextRef.current === activeScope);
    } catch (e) {
      if (mountedRef.current && revision === revisionRef.current) {
        setError(`Could not load saved message history. ${e?.message || String(e)}`);
      }
    } finally {
      if (mountedRef.current && revision === revisionRef.current) setLoading(false);
    }
  }, [branch, userKey, params]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; revisionRef.current += 1; };
  }, []);

  useEffect(() => {
    const update = () => setPending(readPending(branch, userKey).filter((item) => !sourceFilter || item.source === sourceFilter));
    update();
    window.addEventListener("wa:pending-errors", update);
    window.addEventListener("storage", update);
    window.addEventListener("online", load);
    return () => {
      window.removeEventListener("wa:pending-errors", update);
      window.removeEventListener("storage", update);
      window.removeEventListener("online", load);
    };
  }, [branch, sourceFilter, userKey, load]);

  useEffect(() => { setSourceFilter(source); }, [source]);

  useEffect(() => {
    // Clear another branch's data before a new request completes.
    setData({ items: [], total: 0, templates: [] });
    setOffset(0);
    setError("");
    setNotice("");
    setAttempts({});
  }, [branch, source, userKey]);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") load(true);
    }, 5000);
    return () => { clearInterval(timer); revisionRef.current += 1; };
  }, [load, refreshToken]);

  async function toggleAttempts(item) {
    const activeScope = contextRef.current;
    const id = String(item.id);
    if (attempts[id]) {
      setAttempts((previous) => { const next = { ...previous }; delete next[id]; return next; });
      return;
    }
    setAttemptLoading(id);
    try {
      const response = await requestRef.current(`/wa360/messages/${encodeURIComponent(id)}/attempts`, {
        method: "GET", headers: { "x-branch": branch },
      });
      if (mountedRef.current && contextRef.current === activeScope) setAttempts((previous) => ({ ...previous, [id]: Array.isArray(response?.items) ? response.items : [] }));
    } catch (e) {
      if (mountedRef.current) setError(`Could not load the attempt history. ${e?.message || e}`);
    } finally { if (mountedRef.current) setAttemptLoading(""); }
  }

  async function retryRows(rows) {
    if (retryLockRef.current || !rows.length || !branch) return;
    retryLockRef.current = true;
    setError("");
    setNotice("");
    let submitted = 0;
    const problems = [];
    const activeScope = contextRef.current;
    try {
      // Sequential requests keep a bulk click from flooding the provider. The
      // backend checks eligibility and claims each original message atomically.
      for (const row of rows) {
        if (!mountedRef.current || contextRef.current !== activeScope) break;
        if (!retryPresentation(row).eligible) continue;
        setRetrying(String(row.id));
        try {
          await requestRef.current(`/wa360/messages/${encodeURIComponent(row.id)}/retry`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "x-branch": branch },
            body: JSON.stringify({ retry_key: newRetryKey(), branch }),
          });
          submitted += 1;
        } catch (e) {
          problems.push(`${row.customer_name || row.phone || row.id}: ${e?.message || String(e)}`);
        }
      }
      if (mountedRef.current && contextRef.current === activeScope) {
        setNotice(`${submitted} retry request${submitted === 1 ? "" : "s"} processed. Check the updated status below.`);
        if (problems.length) setError(problems.join("\n"));
        await load(true);
      }
    } finally {
      retryLockRef.current = false;
      if (mountedRef.current) setRetrying("");
    }
  }

  const eligible = data.items.filter((item) => retryPresentation(item).eligible);
  const knownTemplates = [...new Set([
    ...data.templates.map((item) => typeof item === "string" ? item : item.template_name || item.name || ""),
    ...data.items.map((item) => item.template_name || ""), template,
  ].filter(Boolean))].sort();
  const knownErrorCodes = [...new Set(["130472", "131049", "131026", "132000", errorCode,
    ...data.items.map((item) => text(item.error_code)),
  ].filter(Boolean))].sort();
  const errorLabels = { "130472": "Experiment restriction", "131049": "Marketing delivery limit", "131026": "Recipient delivery issue", "132000": "Template parameter mismatch" };

  function filter(update) { setOffset(0); setNotice(""); update(); }

  return (
    <section aria-label="Saved WhatsApp message history" style={{ background: "#111827", color: "#e5e7eb", border: "1px solid #334155", borderRadius: 14, padding: 14, marginBottom: 14 }}>
      <h3 style={{ margin: "0 0 6px" }}>Saved sending history</h3>
      <p style={{ color: "#cbd5e1", margin: "0 0 12px", fontSize: 13 }}>
        Customer, template and delivery errors are saved on the server and remain after refreshing this page.
        Accepted means the provider received the request; delivery is confirmed by later status updates.
        Retry availability depends on the provider error. Waiting or customer action may be required.
      </p>
      {!branch ? <p>Select a branch to view its saved messages.</p> : (
        <>
          <form onSubmit={(e) => { e.preventDefault(); filter(() => setAppliedQuery(query.trim())); }} style={{ display: "flex", gap: 8, alignItems: "end", flexWrap: "wrap", marginBottom: 12 }}>
            <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Sending page
              <select style={fieldStyle} value={sourceFilter} onChange={(e) => filter(() => setSourceFilter(e.target.value))}>
                <option value="">All sources</option>
                <option value="birthday">Birthday search</option>
                <option value="customer_service">Search by date</option>
                <option value="campaign">Campaign / CSV</option>
                <option value="wa360">Direct sender</option>
                <option value="wa_sender">Desktop sender</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Status
              <select style={fieldStyle} value={status} onChange={(e) => filter(() => setStatus(e.target.value))}>
                <option value="errors">Errors and uncertain sends</option>
                <option value="">All statuses</option>
                <option value="failed">Failed</option>
                <option value="undeliverable">Undeliverable</option>
                <option value="unknown">Unknown delivery</option>
                <option value="accepted">Accepted by provider</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="read">Read</option>
                <option value="sending">Sending</option>
                <option value="invalid">Invalid / skipped</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Template
              <select style={fieldStyle} value={template} onChange={(e) => filter(() => setTemplate(e.target.value))}>
                <option value="">All templates</option>
                {knownTemplates.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Error code
              <select style={fieldStyle} value={errorCode} onChange={(e) => filter(() => setErrorCode(e.target.value))}>
                <option value="">All error codes</option>
                {knownErrorCodes.map((code) => <option key={code} value={code}>{code}{errorLabels[code] ? ` — ${errorLabels[code]}` : ""}</option>)}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Customer search date
              <input type="date" value={searchDate} onChange={(e) => filter(() => setSearchDate(e.target.value))} style={fieldStyle} />
            </label>
            <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Customer, phone or error
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, phone, error code…" style={{ ...fieldStyle, width: 240 }} />
            </label>
            <button type="submit" style={buttonStyle}>Filter</button>
            <button type="button" style={buttonStyle} onClick={() => load()} disabled={loading}>Refresh</button>
            <button type="button" style={buttonStyle} disabled={!!retrying || !eligible.length} onClick={() => retryRows(eligible)}>
              {retrying ? "Retrying…" : `Retry eligible on this page (${eligible.length})`}
            </button>
          </form>
          {error && <div role="alert" style={{ color: "#fecaca", whiteSpace: "pre-wrap", marginBottom: 10 }}>{error}</div>}
          {notice && <div role="status" style={{ color: "#a7f3d0", marginBottom: 10 }}>{notice}</div>}
          {pending.length > 0 && <div role="status" style={{ color: "#fde68a", marginBottom: 12 }}>
            <b>{pending.length} error(s) waiting to sync.</b> Saved in this browser; syncing automatically when the server is available. Check server history before sending again.
            <ul>{pending.map((item) => <li key={item.request_key}>{item.customer_name || item.phone || "Customer"} · {item.template_name} · {item.error_code}: {item.error_message}</li>)}</ul>
          </div>}
          <div style={{ overflowX: "auto", background: "#fff", color: "#111827", borderRadius: 10 }}>
            <table style={{ borderCollapse: "collapse", minWidth: 1150, width: "100%", fontSize: 13 }}>
              <thead style={{ background: "#e2e8f0" }}><tr>
                {["Customer / phone", "Template / branch", "Status", "Error", "Provider message ID", "Time / attempts", "Next action / retry"].map((label) => <th key={label} scope="col" style={cellStyle}>{label}</th>)}
              </tr></thead>
              <tbody>
                {data.items.map((item) => (
                  <React.Fragment key={item.id}><tr>
                    <td style={cellStyle}><b>{item.customer_name || "Unknown customer"}</b><div>{item.phone || "No phone"}</div><small>{item.search_date || ""}</small></td>
                    <td style={cellStyle}><b>{item.template_name || "—"}</b><div>{item.branch || branch}</div></td>
                    <td style={cellStyle}><b>{text(item.status).replace(/_/g, " ") || "—"}</b></td>
                    <td style={{ ...cellStyle, maxWidth: 340, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {item.error_code && <b>{text(item.error_code)}<br /></b>}
                      {text(item.error_message) || "—"}
                    </td>
                    <td style={{ ...cellStyle, maxWidth: 210, overflowWrap: "anywhere" }}>{item.message_id || "—"}</td>
                    <td style={cellStyle}>{readableTime(item.updated_at || item.created_at)}<div>{Number(item.attempt_count || 1)} attempt(s)</div></td>
                    <td style={{ ...cellStyle, minWidth: 230, maxWidth: 320 }}>
                      <button type="button" style={{ ...buttonStyle, background: retryPresentation(item).eligible ? "#1d4ed8" : "#475569", opacity: !retryPresentation(item).eligible || retrying ? 0.65 : 1 }} disabled={!retryPresentation(item).eligible || !!retrying} onClick={() => retryRows([item])}>
                        {retrying === String(item.id) ? "Retrying…" : retryPresentation(item).label}
                      </button>
                      <RetryGuidance item={item} />
                      <button type="button" style={{ ...buttonStyle, marginTop: 6 }} disabled={attemptLoading === String(item.id)} onClick={() => toggleAttempts(item)}>
                        {attemptLoading === String(item.id) ? "Loading…" : attempts[String(item.id)] ? "Hide attempts" : "View attempts"}
                      </button>
                    </td>
                  </tr>
                  {attempts[String(item.id)] && <tr><td colSpan={7} style={{ ...cellStyle, background: "#f1f5f9" }}>
                    <b>All attempts for {item.customer_name || item.phone}</b>
                    <ol style={{ margin: "8px 0", paddingLeft: 22 }}>
                      {attempts[String(item.id)].map((attempt) => <li key={attempt.id} style={{ marginBottom: 8, overflowWrap: "anywhere" }}>
                        <b>{text(attempt.status).replace(/_/g, " ")}</b> · {readableTime(attempt.created_at)} · Template: {attempt.template_name || "—"}
                        <div>{[text(attempt.error_code), text(attempt.error_message)].filter(Boolean).join(": ") || "No error recorded"}</div>
                        {attempt.correction_note && <div><b>Correction:</b> {text(attempt.correction_note)}</div>}
                        <small>Message ID: {attempt.message_id || "—"}</small>
                      </li>)}
                    </ol>
                  </td></tr>}
                  </React.Fragment>
                ))}
                {!data.items.length && <tr><td colSpan={7} style={cellStyle}>{loading ? "Loading saved messages…" : "No saved messages match these filters."}</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
            <span>{data.total ? `${offset + 1}–${Math.min(offset + data.items.length, data.total)} of ${data.total}` : "0 messages"}{loading ? " · Updating…" : ""}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" style={buttonStyle} disabled={!offset || loading} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
              <button type="button" style={buttonStyle} disabled={offset + PAGE_SIZE >= data.total || loading} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
